import React, {useMemo} from 'react';

import {ResponsiveLine} from '@nivo/line';
import {useStore} from 'effector-react';
import i18n from 'i18next';
import {useTranslation} from 'react-i18next';
import styled, {css} from 'styled-components';

import {Spinner} from 'components/ui-kit/Spinner';
import {dashboardState$, dateRange$, dateType$} from 'models/dashboard';
import {border, breakpoint, colors} from 'theme';
import {DashboardDateType, ProductDynamicRecord} from 'types';
import {formatDate} from 'utils/date';
import {abbreviateNumber, formatNumber} from 'utils/number';

import {BlockTitle, BlockTitleDescription, SpinnerLayer} from '../styled';

import {Root, TooltipContainer} from './styled';

const CustomRoot = styled(Root)`
    position: relative;
`;

const ChartContainer = styled.div`
    height: 250px;
`;

const TooltipLabel = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 7px 0;

    &:not(:last-of-type) {
        border-bottom: 1px solid #575757;
    }
`;

const ChartBackground = styled.div`
    display: block;
    height: 30px;
    margin-left: -30px;
    margin-right: -30px;
    border-bottom-left-radius: ${border.radius};
    border-bottom-right-radius: ${border.radius};

    @media (min-width: ${breakpoint.tablet}) {
        height: 20px;
    }
`;

const LegendLabel = styled.span<{isLeft: boolean}>`
    position: absolute;

    ${(p) =>
        p.isLeft
            ? css`
                  left: 0;
              `
            : css`
                  right: 0;
              `}
    bottom: 0px;
    font-size: 12px;
    line-height: 0;
    margin-bottom: -7px;
    color: ${colors.black};
`;

type Props = {
    data: ProductDynamicRecord[];
    total: number | null;
    className?: string;
};

const Tooltip = ({slice}: {slice: any}) => {
    const {t} = useTranslation();
    const dateType = useStore(dateType$);
    const point = slice.points[0].data;

    const dayName = formatDate(point.rawX, 'EEEE');

    const label = () => {
        switch (dateType) {
            case DashboardDateType.Day:
                return formatDate(point.rawX, 'HH:mm');
            case DashboardDateType.Week:
                return `${formatDate(point.rawX, 'dd.MM.yyyy')}, ${dayName}`;
            case DashboardDateType.Month:
                return `${formatDate(point.rawX, 'dd.MM.yyyy')}, ${dayName}`;
            case DashboardDateType.Quarter:
            case DashboardDateType.Period:
                return formatDate(point.rawX, 'dd.MM.yyyy');
        }
    };

    return (
        <TooltipContainer isWide>
            <TooltipLabel>{label()}</TooltipLabel>
            <TooltipLabel>
                <span>{t('pages.dashboard.affixed')}:</span>{' '}
                <span>{formatNumber(point.yFormatted)}</span>
            </TooltipLabel>
        </TooltipContainer>
    );
};

export function ProductDynamicChart({data, total, className}: Props) {
    const {t} = useTranslation();
    const dateType = useStore(dateType$);
    const {productDynamicPending} = useStore(dashboardState$);

    const gridValues = useMemo(() => {
        if (!data.length) return [];

        const max = Math.max(...(data ?? []).map((d) => d.value)) ?? 0;

        const targetLines = 7;

        const rawStep = max / targetLines;
        const magnitude = 10 ** Math.floor(Math.log10(rawStep));
        const normalized = rawStep / magnitude;

        let niceStep;
        if (normalized <= 1) niceStep = magnitude;
        else if (normalized <= 2) niceStep = 2 * magnitude;
        else if (normalized <= 5) niceStep = 5 * magnitude;
        else niceStep = 10 * magnitude;
        const result: number[] = [];
        if (!niceStep) return result;
        for (let v = 0; v <= max + niceStep; v += niceStep) {
            result.push(v);
        }

        return result;
    }, [data]);

    const xGridValues = useMemo(() => {
        if (!data.length) return [];

        const maxTicksMapping = {
            [DashboardDateType.Day]: 8,
            [DashboardDateType.Week]: 7,
            [DashboardDateType.Month]: 8,
            [DashboardDateType.Quarter]: 7,
            [DashboardDateType.Period]: 7,
        };

        const maxTicks = maxTicksMapping[dateType];
        const tickStep = Math.max(1, Math.ceil((data.length - 1) / maxTicks));

        return data.reduce((acc, value, i, array) => {
            const isFirst = i === 0;
            const isLast = i === array.length - 1;
            const shouldShowIntermediate = !isFirst && !isLast && i % tickStep === 0;

            if (isFirst || isLast || shouldShowIntermediate) {
                acc.push(new Date(value.time));
            }

            return acc;
        }, [] as Date[]);
    }, [data, dateType]);

    const mappedData = [
        {
            id: 'Dynamic',
            data: data.map(({time, value}) => ({
                x: new Date(time),
                rawX: time,
                y: value,
            })),
        },
    ];

    return (
        <CustomRoot className={className}>
            <BlockTitle>{t('pages.dashboard.dynamicChart')}</BlockTitle>
            {productDynamicPending ? (
                <SpinnerLayer>
                    <Spinner />
                </SpinnerLayer>
            ) : (
                <>
                    {/*{dateRange.start && (*/}
                    {/*    <LegendLabel isLeft={true}>{formatDate(dateRange.start)}</LegendLabel>*/}
                    {/*)}*/}
                    {/*{dateRange.end && (*/}
                    {/*    <LegendLabel isLeft={false}>{formatDate(dateRange.end)}</LegendLabel>*/}
                    {/*)}*/}
                    <BlockTitleDescription>
                        {t('pages.dashboard.total')}: {formatNumber(total ?? 0)}
                    </BlockTitleDescription>
                    <ChartContainer>
                        <ResponsiveLine
                            data={mappedData}
                            margin={{top: 20, right: 12, bottom: 40, left: 56}}
                            enableSlices="x"
                            xScale={{type: 'time'}}
                            yScale={{
                                type: 'linear',
                                min: 'auto',
                                max: 'auto',
                                stacked: true,
                                reverse: false,
                            }}
                            theme={{
                                axis: {
                                    ticks: {
                                        text: {
                                            fontSize: 10,
                                            fill: colors.textSecondary,
                                        },
                                    },
                                },
                            }}
                            gridYValues={gridValues}
                            gridXValues={xGridValues}
                            sliceTooltip={({slice}) => <Tooltip slice={slice} />}
                            curve="monotoneX"
                            axisTop={null}
                            axisRight={null}
                            colors={colors.brandPrimary}
                            enableGridX={false}
                            axisBottom={{
                                tickValues: xGridValues,
                                tickSize: 5,
                                tickPadding: 8,
                                tickRotation: data.length > 8 ? -30 : 0,
                                format: (value) =>
                                    formatDate(
                                        value as Date,
                                        dateType === DashboardDateType.Day ? 'HH:mm' : 'dd.MM.yy',
                                    ),
                            }}
                            enableArea={true}
                            areaOpacity={1}
                            areaBaselineValue={0}
                            enablePoints={false}
                            axisLeft={{
                                tickValues: gridValues,
                                tickSize: 0,
                                tickPadding: 12,
                                tickRotation: 0,
                                format: (value) => {
                                    if (!value) return '';
                                    const roundedValue =
                                        Math.round(((value as number) + Number.EPSILON) * 100) /
                                        100;

                                    return abbreviateNumber(roundedValue, i18n.language);
                                },
                            }}
                            useMesh={true}
                            defs={[
                                {
                                    id: 'gradientArea',
                                    type: 'linearGradient',
                                    colors: [
                                        {offset: 0, color: colors.brandPrimary, opacity: 1},
                                        {offset: 100, color: '#ffffff', opacity: 0.0},
                                    ],
                                },
                            ]}
                            fill={[{match: '*', id: 'gradientArea'}]}
                        />
                    </ChartContainer>
                </>
            )}
        </CustomRoot>
    );
}
